import {CommonModule} from '@angular/common';
import {Component, Inject, OnInit} from '@angular/core';
import {FormBuilder, FormGroup, ReactiveFormsModule} from '@angular/forms';
import {
  AntifraudProviderSettings,
  AntifraudSettings,
  DomainSetting,
} from '@gatewee/types/domain-setting';
import {RecipientSetting} from '@gatewee/types/recipient-setting';
import {PaymentProvider} from '@gatewee/types/provider';
import {TranslateModule, TranslateService} from '@ngx-translate/core';
import {TuiAlertService, TuiDialogContext} from '@taiga-ui/core';
import {POLYMORPHEUS_CONTEXT} from '@taiga-ui/polymorpheus';
import {Observable} from 'rxjs';
import {GateweeButtonComponent} from '../../gatewee-button/gatewee-button.component';
import {GateweeInputV2Component} from '../../gatewee-input-v2/gatewee-input-v2.component';

interface DialogData {
  id: number;
  setting: RecipientSetting | DomainSetting;
  editSettingFn: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;
}

@Component({
  selector: 'antifraud-parameter',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    GateweeButtonComponent,
    GateweeInputV2Component,
  ],
  templateUrl: './antifraud-parameter.component.html',
})
export class AntifraudParameterComponent implements OnInit {
  formGroup!: FormGroup;
  submitting = false;
  id!: number;
  setting!: RecipientSetting | DomainSetting;
  editSettingFn!: (
    id: number,
    setting: RecipientSetting | DomainSetting,
  ) => Observable<void>;

  readonly providers = Object.values(PaymentProvider);

  constructor(
    private fb: FormBuilder,
    private alerts: TuiAlertService,
    private translate: TranslateService,
    @Inject(POLYMORPHEUS_CONTEXT)
    private readonly context: TuiDialogContext<boolean, DialogData>,
  ) {
    const data = this.context.data;
    this.id = data.id;
    this.setting = data.setting;
    this.editSettingFn = data.editSettingFn;
  }

  ngOnInit(): void {
    const parsed = this.parseValue();
    const children: Record<string, FormGroup> = {};
    for (const p of this.providers) {
      const cur = parsed[p] ?? {enabled: false};
      children[p] = this.fb.group({
        enabled: [cur.enabled ?? false],
        publicKey: [cur.credentials?.publicKey ?? ''],
        deviceFingerprintOrgId: [cur.credentials?.deviceFingerprintOrgId ?? ''],
        deviceFingerprintSessionPrefix: [
          cur.credentials?.deviceFingerprintSessionPrefix ?? '',
        ],
      });
    }
    this.formGroup = this.fb.group(children);
  }

  providerGroup(p: string): FormGroup {
    return this.formGroup.get(p) as FormGroup;
  }

  private parseValue(): Partial<
    Record<PaymentProvider, AntifraudProviderSettings>
  > {
    const raw = this.setting.value;
    if (!raw) return {};
    try {
      const obj =
        typeof raw === 'string'
          ? (JSON.parse(raw) as AntifraudSettings)
          : (raw as AntifraudSettings);
      return typeof obj === 'object' && obj !== null ? obj : {};
    } catch {
      return {};
    }
  }

  onSubmit(): void {
    if (this.submitting) return;
    this.submitting = true;
    const out: Partial<Record<PaymentProvider, AntifraudProviderSettings>> = {};
    for (const p of this.providers) {
      const g = this.providerGroup(p).value as {
        enabled: boolean;
        publicKey: string;
        deviceFingerprintOrgId: string;
        deviceFingerprintSessionPrefix: string;
      };
      const entry: AntifraudProviderSettings = {
        enabled: Boolean(g.enabled),
      };
      const cred: NonNullable<AntifraudProviderSettings['credentials']> = {};
      if (g.publicKey?.trim()) cred.publicKey = g.publicKey.trim();
      if (g.deviceFingerprintOrgId?.trim()) {
        cred.deviceFingerprintOrgId = g.deviceFingerprintOrgId.trim();
      }
      if (g.deviceFingerprintSessionPrefix?.trim()) {
        cred.deviceFingerprintSessionPrefix =
          g.deviceFingerprintSessionPrefix.trim();
      }
      if (Object.keys(cred).length > 0) {
        entry.credentials = cred;
      }
      out[p as PaymentProvider] = entry;
    }
    const updatedSetting: RecipientSetting | DomainSetting = {
      ...this.setting,
      value: JSON.stringify(out),
    };
    this.editSettingFn(this.id, updatedSetting).subscribe({
      next: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateSuccess', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.success'),
              appearance: 'success',
            },
          )
          .subscribe();
        this.context.completeWith(true);
      },
      error: () => {
        this.alerts
          .open(
            this.translate.instant('features.settings.updateError', {
              key: this.setting.key,
            }),
            {
              label: this.translate.instant('common.status.error'),
              appearance: 'error',
            },
          )
          .subscribe();
        this.submitting = false;
      },
    });
  }

  close(): void {
    this.context.completeWith(false);
  }
}
